import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import {
  AutocompleteInputComponent,
  AutocompleteItem,
} from '@delmonicos/shared-design-system-react/src/components/autocomplete-input/autocomplete-input.component';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Checkbox } from '@delmonicos/shared-design-system-react/src/components/checkbox/checkbox';
import { CountryCode } from '@delmonicos/shared-design-system-react/src/components/country-code/country-code';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { useGatewayCreateCustomerFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/customers/models/create-customer.form-validation';
import { GatewayCreateCustomerModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/customers/models/create-customer.model';
import { formatISO } from 'date-fns';
import { useEffect, useState } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCpmsBoGetChargePointSubscriptionsQuery } from '@service/cpms-bo-charge-point-subscription.service';

const CustomerCreateFormStep2CommissionFragment = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { commissionPercentage } = useGatewayCreateCustomerFormValidation();
  const { control } = useFormContext<GatewayCreateCustomerModel>();

  return (
    <Card gap={GapEnum.M} flex={1}>
      <AccentTitle
        icon="currency-euro"
        text={t('$.customer-create.commissions')}
      />
      <Controller
        name="commissionPercentage"
        rules={commissionPercentage}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t(
              'delmonicos-shared-sdk.outputs-customer.commission-percentage',
            )}
            placeholder={t(
              'delmonicos-shared-sdk.outputs-customer.commission-percentage',
            )}
            type="number"
            required={!!commissionPercentage.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(
                e.target.value === '' ? '' : e.target.valueAsNumber,
              );
            }}
            adornment="%"
          />
        )}
      />
    </Card>
  );
};

const CustomerCreateFormStep2SubscriptionSelection = ({
  field,
  fieldState: { error },
}: {
  field: ControllerRenderProps<GatewayCreateCustomerModel, 'subscriptionId'>;
  fieldState: ControllerFieldState;
}): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { subscriptionId } = useGatewayCreateCustomerFormValidation();

  // SEARCH TERM ///////////////////////////////////////////////////////////////

  const [search, setSearch] = useState<string>('');

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const { data: subscriptions, isSuccess } =
    useCpmsBoGetChargePointSubscriptionsQuery({
      limit: 10,
      offset: 0,
      name: `~${search}`,
    });

  // AUTOCOMPLETE ITEM /////////////////////////////////////////////////////////

  const [selectedItem, setSelectedItem] = useState<AutocompleteItem | null>(
    null,
  );

  useEffect(() => {
    if (isSuccess) {
      const subscription =
        subscriptions?.docs.find((s) => s?.id === field.value) ?? null;

      if (subscription) {
        setSelectedItem({ key: subscription.id, label: subscription.name });
        return;
      }

      setSelectedItem(null);
    }
  }, [search, subscriptions, isSuccess, field.value]);

  // AUTOCOMPLETE ITEMS ////////////////////////////////////////////////////////

  const [autocompleteItems, setAutocompleteItems] = useState<
    AutocompleteItem[]
  >([]);

  useEffect(() => {
    if (isSuccess) {
      setAutocompleteItems(
        subscriptions?.docs.map((s) => ({
          key: s.id,
          label: s.name,
        })) ?? [],
      );
    }
  }, [search, subscriptions, isSuccess]);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <AutocompleteInputComponent
      label={t('delmonicos-shared-sdk.outputs-gateway-customer.subscription')}
      placeholder={t(
        'delmonicos-shared-sdk.outputs-gateway-customer.subscription',
      )}
      required={!!subscriptionId.required}
      value={search}
      onChange={setSearch}
      onSearch={setSearch}
      items={autocompleteItems}
      selectedItem={selectedItem}
      onSelect={(selectedItemNext) => {
        field.onChange(selectedItemNext?.key ?? '');
      }}
      emptyResultText={t('global.no-result-found')}
      errorMessage={error?.message}
    />
  );
};

const CustomerCreateFormStep2SubscriptionFragment = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { subscriptionId } = useGatewayCreateCustomerFormValidation();
  const { control } = useFormContext<GatewayCreateCustomerModel>();

  return (
    <Card gap={GapEnum.M} flex={1}>
      <AccentTitle
        icon="receipt-check"
        variant={AccentVariantEnum.Secondary}
        text={t('delmonicos-shared-sdk.outputs-gateway-customer.subscription')}
      />

      <Controller
        name="subscriptionId"
        control={control}
        rules={subscriptionId}
        render={CustomerCreateFormStep2SubscriptionSelection}
      />
    </Card>
  );
};

const CustomerCreateFormStep2BillingFragment = (): React.JSX.Element => {
  const { t } = useTranslation();
  const {
    billingDetailsFirstName,
    billingDetailsLastName,
    billingDetailsAddressAddress,
    billingDetailsAddressCity,
    billingDetailsAddressZipCode,
    billingDetailsAddressCountryCode,
    isExemptFromBilling,
  } = useGatewayCreateCustomerFormValidation();
  const { control } = useFormContext<GatewayCreateCustomerModel>();

  return (
    <Card flex={1} gap={GapEnum.M} direction="column">
      <AccentTitle
        icon="receipt-check"
        variant={AccentVariantEnum.Accent1}
        text={t('$.customer-create.billing')}
      />
      <Stack gap={GapEnum.M} justifyContent="space-between" flexWrap="wrap">
        <Controller
          name="billingDetails.firstName"
          rules={billingDetailsFirstName}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t(
                'delmonicos-shared-sdk.outputs-billing-details.first-name',
              )}
              placeholder={t(
                'delmonicos-shared-sdk.outputs-billing-details.first-name',
              )}
              required={!!billingDetailsFirstName.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
        <Controller
          name="billingDetails.lastName"
          rules={billingDetailsLastName}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t(
                'delmonicos-shared-sdk.outputs-billing-details.last-name',
              )}
              placeholder={t(
                'delmonicos-shared-sdk.outputs-billing-details.last-name',
              )}
              required={!!billingDetailsLastName.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
      </Stack>

      <Controller
        name="billingDetails.address.address"
        rules={billingDetailsAddressAddress}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-address.address')}
            placeholder={t('delmonicos-shared-sdk.outputs-address.address')}
            required={!!billingDetailsAddressAddress.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value === '' ? '' : e.target.value);
            }}
          />
        )}
      />

      <Stack gap={GapEnum.M} justifyContent="space-between" flexWrap="wrap">
        <Controller
          name="billingDetails.address.zipCode"
          rules={billingDetailsAddressZipCode}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-address.zip-code')}
              placeholder={t('delmonicos-shared-sdk.outputs-address.zip-code')}
              required={!!billingDetailsAddressZipCode.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
        <Controller
          name="billingDetails.address.city"
          rules={billingDetailsAddressCity}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Stack flex={1}>
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-address.city')}
                placeholder={t('delmonicos-shared-sdk.outputs-address.city')}
                required={!!billingDetailsAddressCity.required}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value === '' ? '' : e.target.value);
                }}
              />
            </Stack>
          )}
        />
        <Controller
          name="billingDetails.address.countryCode"
          rules={billingDetailsAddressCountryCode}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <CountryCode
              errorMessage={error?.message}
              fieldValue={field.value}
              fieldOnChange={field.onChange}
            />
          )}
        />
      </Stack>

      <Controller
        name="isExemptFromBilling"
        rules={isExemptFromBilling}
        control={control}
        render={({ field }) => (
          <Checkbox
            id="isExemptFromBilling"
            label={t(
              'delmonicos-shared-sdk.outputs-customer.is-exempt-from-billing',
            )}
            checked={field.value}
            onChange={(e) => field.onChange(e.target.checked)}
          />
        )}
      />
    </Card>
  );
};

const CustomerCreateFormStep2BankFragment = (): React.JSX.Element => {
  const { t } = useTranslation();
  const {
    billingDetailsIban,
    billingDetailsBic,
    billingDetailsMandateDateOfSignature,
    billingDetailsMandateDocumentNumber,
  } = useGatewayCreateCustomerFormValidation();
  const { control, trigger, watch } =
    useFormContext<GatewayCreateCustomerModel>();
  const sepaDateValue = watch('billingDetails.mandate.dateOfSignature');
  const sepaDocumentNumberValue = watch(
    'billingDetails.mandate.documentNumber',
  );

  return (
    <Card flex={1} gap={GapEnum.M} direction="column">
      <AccentTitle
        icon="bank"
        text={t('delmonicos-shared-sdk.outputs-customer.billing-details')}
        variant={AccentVariantEnum.Accent1}
      />

      <Controller
        name="billingDetails.iban"
        rules={billingDetailsIban}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-billing-details.iban')}
            placeholder={t(
              'delmonicos-shared-sdk.outputs-billing-details.iban',
            )}
            required={!!billingDetailsIban.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value === '' ? '' : e.target.value);
            }}
          />
        )}
      />
      <Controller
        name="billingDetails.bic"
        rules={billingDetailsBic}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-billing-details.bic')}
            placeholder={t('delmonicos-shared-sdk.outputs-billing-details.bic')}
            required={!!billingDetailsBic.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value === '' ? '' : e.target.value);
            }}
          />
        )}
      />

      <Divider />

      <h2>{t('delmonicos-shared-sdk.outputs-billing-details.mandate')}</h2>

      <Controller
        name="billingDetails.mandate.documentNumber"
        rules={billingDetailsMandateDocumentNumber}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t(
              'delmonicos-shared-sdk.outputs-sepa-mandate.document-number',
            )}
            placeholder={t(
              'delmonicos-shared-sdk.outputs-sepa-mandate.document-number',
            )}
            required={!!sepaDateValue || !!sepaDocumentNumberValue}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value === '' ? '' : e.target.value);
              trigger([
                'billingDetails.mandate.documentNumber',
                'billingDetails.mandate.dateOfSignature',
              ]);
            }}
          />
        )}
      />
      <Controller
        name="billingDetails.mandate.dateOfSignature"
        rules={billingDetailsMandateDateOfSignature}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t(
              'delmonicos-shared-sdk.outputs-sepa-mandate.date-of-signature',
            )}
            placeholder={t(
              'delmonicos-shared-sdk.outputs-sepa-mandate.date-of-signature',
            )}
            type="date"
            required={!!sepaDateValue || !!sepaDocumentNumberValue}
            errorMessage={error?.message}
            {...field}
            value={
              field.value ? formatISO(new Date(field.value)).split('T')[0] : ''
            }
            onChange={(e) => {
              field.onChange(new Date(e.target.value));
              trigger([
                'billingDetails.mandate.documentNumber',
                'billingDetails.mandate.dateOfSignature',
              ]);
            }}
          />
        )}
      />
    </Card>
  );
};

export const CustomerCreateFormStep2Controller = (): React.JSX.Element => {
  return (
    <Stack gap={GapEnum.L} flex={1}>
      <Stack gap={GapEnum.L} flex={1} direction="column">
        <Stack flex={1}>
          <CustomerCreateFormStep2CommissionFragment />
        </Stack>
        <Stack flex={1}>
          <CustomerCreateFormStep2SubscriptionFragment />
        </Stack>
      </Stack>
      <Stack flex={1}>
        <CustomerCreateFormStep2BillingFragment />
      </Stack>
      <Stack flex={1}>
        <CustomerCreateFormStep2BankFragment />
      </Stack>
    </Stack>
  );
};
